import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { sendFirebaseOtp, toE164 } from "@/lib/firebasePhone";

export default function FirebasePhoneBox({ config, onVerified, verifiedPhone, testPrefix = "fotp" }) {
  const boxRef = useRef(null);
  const confirmRef = useRef(null);
  const [local, setLocal] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const send = async () => {
    const phone = toE164(local);
    if (!phone) {
      setError("Enter a valid mobile number.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      confirmRef.current = await sendFirebaseOtp(config, phone, boxRef.current);
      setSent(true);
    } catch (e) {
      setError(e?.message || "Could not send the code.");
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!confirmRef.current) return;
    setBusy(true);
    setError("");
    try {
      const cred = await confirmRef.current.confirm(code.trim());
      const token = await cred.user.getIdToken();
      onVerified({ token, phone: cred.user.phoneNumber || toE164(local) });
    } catch (e) {
      setError(e?.message || "That code is incorrect.");
    } finally {
      setBusy(false);
    }
  };

  if (verifiedPhone) {
    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800" data-testid={`${testPrefix}-verified`}>
        Mobile verified {verifiedPhone}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-box`}>
      <div>
        <Label htmlFor={`${testPrefix}-phone`}>Mobile number</Label>
        <div className="mt-1 flex gap-2">
          <span className="inline-flex h-11 items-center rounded-md border border-stone-200 bg-stone-50 px-3 text-sm text-stone-600 shrink-0">+91</span>
          <Input
            id={`${testPrefix}-phone`}
            inputMode="numeric"
            autoComplete="tel"
            placeholder="10-digit mobile"
            value={local}
            onChange={(e) => setLocal(e.target.value)}
            className="h-11"
            data-testid={`${testPrefix}-phone`}
          />
        </div>
      </div>
      <div ref={boxRef} className="min-h-[78px] overflow-x-auto" data-testid={`${testPrefix}-recaptcha`} />
      {!sent ? (
        <Button type="button" variant="outline" className="w-full h-11" disabled={busy} onClick={send} data-testid={`${testPrefix}-send`}>
          {busy ? "Sending code…" : "Send OTP"}
        </Button>
      ) : (
        <div className="space-y-2">
          <Label htmlFor={`${testPrefix}-code`}>OTP</Label>
          <Input
            id={`${testPrefix}-code`}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6-digit code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className="h-11"
            data-testid={`${testPrefix}-code`}
          />
          <Button type="button" className="w-full h-11 btn-store-primary rounded-full" disabled={busy || code.trim().length < 4} onClick={confirm} data-testid={`${testPrefix}-confirm`}>
            {busy ? "Checking…" : "Verify mobile"}
          </Button>
          <button type="button" className="text-xs text-stone-500 underline" onClick={() => { setSent(false); setCode(""); confirmRef.current = null; }}>
            Use a different number
          </button>
        </div>
      )}
      {error ? <p className="text-sm text-red-600" data-testid={`${testPrefix}-error`}>{error}</p> : null}
    </div>
  );
}
